/**
 * The shell-less layouts.
 *
 * Marketing, auth and graduation render outside the app shell on purpose: none
 * of them has a project scope, and a rail full of destinations a signed out
 * visitor cannot reach is furniture pretending to be product.
 *
 * THE FOOTER IS A COMPONENT NOW, not two lines of markup in this file. It used
 * to be the SV terminal tile and the words "Part of the SV family", which was
 * the right amount of footer for a prototype with one public page and no
 * privacy policy to point at. It is not the right amount for a page that asks
 * a stranger for an email address. See SiteFooter.tsx for what replaced it and
 * why. The tile itself still appears there and nowhere else: it is SV
 * Academy's mark, and it has never been SV Cloud's.
 *
 * THE CONSENT CARD IS ON EVERY LAYOUT, this one and AppShell. It decides for
 * itself whether to show (lib/analytics/consent.ts shouldAskConsent): never on
 * a page reached through a link carrying a secret, such as /u, /claim and
 * /invite, and never once answered. Analytics cannot start without it, so a
 * layout that left it out would be a layout where nobody could say yes.
 */

import { Outlet } from 'react-router';

import { ConsentNotice } from '../patterns/ConsentNotice';
import { SiteFooter } from './SiteFooter';
import { t } from '../../lib/i18n/t';

interface BareLayoutProps {
  wide?: boolean;
  /**
   * The layout stops measuring and the screen measures itself.
   *
   * Marketing is the only screen that wants this, and it wants it for one
   * reason: the landing ends on a full bleed band of ink, and a band that has
   * to reach both edges of the window cannot be born inside a centred column.
   * Doing it with a viewport width and a negative margin is the usual trick and
   * it is also the usual horizontal scrollbar, because 100vw counts a classic
   * scrollbar that the document does not have. Handing the whole width to the
   * screen and letting each chapter set its own gutter has no such failure
   * mode: every length involved is a percentage of a box that is already the
   * right width.
   */
  bleed?: boolean;
}

export function BareLayout({ wide = false, bleed = false }: BareLayoutProps) {
  const inner = ['bare__inner'];
  if (wide) inner.push('bare__inner--wide');
  if (bleed) inner.push('bare__inner--bleed');

  return (
    <div className="bare">
      <a className="skip-link" href="#content">
        {t('Skip to content')}
      </a>
      <main className={inner.join(' ')} id="content">
        <Outlet />
      </main>
      <SiteFooter />
      {/* Last in the document, which is where it is on the screen. The card is
          fixed to the bottom of the viewport, and putting it earlier in the
          markup would put it earlier in the tab order too, so the first key
          press on a page a person came to read would land on a card asking
          them about storage. */}
      <ConsentNotice />
    </div>
  );
}

export function MarketingLayout() {
  return <BareLayout wide bleed />;
}

export function AuthLayout() {
  return <BareLayout />;
}

export function GraduationLayout() {
  return <BareLayout />;
}
